import { motion } from 'framer-motion';
import Image from 'next/image';
import { useTranslation } from 'next-i18next';

import Header from '@/common/header';
import { FaqAccordion } from '@/components/trial/shared/faq-accordion';
import { PingCounter } from '@/components/trial/shared/ping-counter';
import { bai_jamjuree } from '@/public/assets/fonts/Bai-Jamjuree';
import { outfit } from '@/public/assets/fonts/Outfit';

import CreateAccountV2 from '../../create-account-v2';
import { Footer } from '../../footer';
import { NoPingAppVideo } from '../../shared/noping-app-video';
import type { DefaultTrialProps } from '../../trial-layout-switcher';
import { HyperspaceBg, StarFieldBg } from './components/backgrounds';
import { EASE_OUT, GAME_IMAGES, PRO_ENDORSEMENTS, SABER_BLUE, SABER_GREEN, SPACE_BLACK, STAR_GRAY, SW_TOOLS_COUNT, TEXT_LIGHT } from './components/constants';
import { DarkVsLight, HyperspaceRouting, OpeningCrawl, ShieldGenerator } from './components/interactive';
import { HoloCard, Section, SectionTitle } from './components/ui-helpers';

interface EnterTheArenaProps extends DefaultTrialProps {}

export function EnterTheArena({ daysTrial, trialConfigId, trialPageId, isDisabledButton = false }: EnterTheArenaProps) {
  const { t } = useTranslation(['components/trial/lp/v4b']);

  const faqItems = Array.from({ length: 6 }, (_, i) => ({
    q: t(`faq.items.${i}.q`),
    a: t(`faq.items.${i}.a`),
  }));

  return (
    <div className={`${bai_jamjuree.className} ${outfit.variable} relative min-h-screen`} style={{ background: SPACE_BLACK, color: TEXT_LIGHT }}>
      <StarFieldBg />

      <Header variant='primary' />

      {/* SECTION 1 - HERO */}
      <section id='hero' className='relative flex min-h-screen items-center px-4 pt-20 md:px-8' style={{ zIndex: 1 }}>
        <div className='relative mx-auto grid w-full max-w-7xl grid-cols-1 items-stretch gap-8 lg:grid-cols-2 lg:gap-12'>
          <div>
            <motion.p className='mb-3 text-sm font-semibold uppercase tracking-[0.3em]' style={{ color: SABER_BLUE, fontFamily: 'var(--font-outfit)' }} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1, ease: EASE_OUT }}>
              {t('hero.subtitle')}
            </motion.p>

            <motion.h1 className='mb-3 text-3xl font-bold leading-tight md:text-4xl lg:text-5xl' style={{ fontFamily: 'var(--font-bai)', color: TEXT_LIGHT }} initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2, ease: EASE_OUT }}>
              {t('hero.title_prefix')} <span style={{ color: SABER_GREEN }}>{t('hero.title_accent')}</span>
            </motion.h1>

            <motion.p className='mb-3 max-w-lg text-base leading-relaxed md:text-lg' style={{ color: STAR_GRAY, fontFamily: 'var(--font-outfit)' }} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.35, ease: EASE_OUT }}>
              {t('hero.description')}
            </motion.p>

            <motion.div className='mb-3' initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5, delay: 0.5, ease: EASE_OUT }}>
              <PingCounter startPing={187} endPing={5} duration={2.5} accentColor={SABER_GREEN} size='lg' labelOptimizing={t('ping.optimizing')} labelDone={t('ping.done')} />
            </motion.div>

            <motion.div className='relative overflow-hidden rounded-xl border' style={{ borderColor: `${SABER_BLUE}40`, boxShadow: `0 0 40px ${SABER_BLUE}20, 0 0 80px ${SABER_BLUE}10` }} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.65, ease: EASE_OUT }}>
              <NoPingAppVideo className='contrast-110 h-auto w-full brightness-90' />
              <motion.div className='absolute inset-0' style={{ background: `linear-gradient(transparent 50%, ${SABER_BLUE}05 50%)`, backgroundSize: '100% 4px' }} animate={{ opacity: [0.3, 0.5, 0.3, 0.4, 0.3] }} transition={{ duration: 4, repeat: Infinity, ease: 'linear' as const }} />
            </motion.div>
          </div>

          <motion.div id='hero-form' className='relative flex flex-col pb-6' initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.7, delay: 0.3, ease: EASE_OUT }}>
            <HoloCard className='flex flex-1 flex-col justify-center p-6 md:p-8' glow={SABER_GREEN} opaque>
              <h2 className='mb-2 text-center text-2xl font-bold md:text-3xl' style={{ fontFamily: 'var(--font-bai)', color: TEXT_LIGHT }}>
                {t('hero.form_title')}
              </h2>
              <p className='mb-5 text-center text-sm' style={{ color: STAR_GRAY, fontFamily: 'var(--font-outfit)' }}>
                {t('hero.form_subtitle')}
              </p>

              <CreateAccountV2 daysTrial={daysTrial} customTrialData={{ custom: trialConfigId, trialPageId }} isDisabledButton={isDisabledButton} formId='v4b-hero-form' accentColor={SABER_GREEN} />

              <div className='mt-4 flex items-center justify-center gap-5 pt-4' style={{ borderTop: `1px solid ${SABER_BLUE}15` }}>
                <span className='text-[10px] uppercase tracking-wider' style={{ color: STAR_GRAY, fontFamily: 'var(--font-outfit)' }}>
                  {t('hero.certified_by')}
                </span>
                <div className='flex items-center gap-1.5'>
                  <svg width='16' height='16' viewBox='0 0 21 21' className='shrink-0'>
                    <rect x='1' y='1' width='9' height='9' fill='#F25022' />
                    <rect x='11' y='1' width='9' height='9' fill='#7FBA00' />
                    <rect x='1' y='11' width='9' height='9' fill='#00A4EF' />
                    <rect x='11' y='11' width='9' height='9' fill='#FFB900' />
                  </svg>
                  <span className='text-[10px] font-semibold' style={{ color: '#A0A0B0', fontFamily: 'var(--font-outfit)' }}>
                    {t('hero.microsoft_certified')}
                  </span>
                </div>
                <div className='flex items-center gap-1.5'>
                  <svg width='16' height='16' viewBox='0 0 24 24' fill='none' className='shrink-0'>
                    <path d='M12 2L3 7v5c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V7l-9-5z' fill='#00A4EF' opacity='0.2' />
                    <path d='M12 2L3 7v5c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V7l-9-5z' stroke='#00A4EF' strokeWidth='1.5' fill='none' />
                    <path d='M9 12l2 2 4-4' stroke='#fff' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
                  </svg>
                  <span className='text-[10px] font-semibold' style={{ color: '#A0A0B0', fontFamily: 'var(--font-outfit)' }}>
                    {t('hero.digicert_secured')}
                  </span>
                </div>
              </div>
            </HoloCard>

            <div className='mt-4 flex flex-wrap justify-center gap-2 px-4'>
              {[
                { value: '80%', labelKey: 'hero.badge_ping' },
                { value: '110%', labelKey: 'hero.badge_fps' },
                { value: '6+', labelKey: 'hero.badge_multi_net' },
                { value: '5+', labelKey: 'hero.badge_multiconn' },
              ].map((b, i) => (
                <div key={i} className='flex items-center gap-1.5 rounded-full border px-3 py-1.5 shadow-lg' style={{ backgroundColor: '#0c0e14', borderColor: `${SABER_GREEN}40`, boxShadow: `0 0 12px ${SABER_GREEN}15` }}>
                  <span className='text-xs font-bold' style={{ color: SABER_GREEN, fontFamily: 'var(--font-bai)' }}>
                    {b.value}
                  </span>
                  <span className='text-[9px] uppercase tracking-wider' style={{ color: STAR_GRAY, fontFamily: 'var(--font-outfit)' }}>
                    {t(b.labelKey)}
                  </span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* SECTION 2 - OPENING CRAWL */}
      <section className='relative px-4 py-16 md:px-8 md:py-24' style={{ zIndex: 1 }}>
        <div className='mx-auto max-w-6xl'>
          <OpeningCrawl />
        </div>
      </section>

      {/* SECTION 3 - DARK SIDE VS LIGHT */}
      <Section id='dark-vs-light'>
        <SectionTitle subtitle={t('dark_vs_light.subtitle')} title={t('dark_vs_light.title')} description={t('dark_vs_light.description')} accentColor={SABER_BLUE} />
        <DarkVsLight />
      </Section>

      {/* SECTION 4 - HYPERSPACE ROUTING */}
      <Section id='hyperspace' bg={<HyperspaceBg />}>
        <SectionTitle subtitle={t('hyperspace.subtitle')} title={t('hyperspace.title')} description={t('hyperspace.description')} accentColor={SABER_BLUE} />
        <HyperspaceRouting />

        <div className='mt-6 grid grid-cols-1 gap-4 md:grid-cols-3'>
          {Array.from({ length: 3 }, (_, i) => (
            <HoloCard key={i} className='p-6' glow={SABER_BLUE} delay={0.08 * i}>
              <span className='mb-3 block text-3xl'>{t(`hyperspace.cards.${i}.icon`)}</span>
              <h3 className='mb-2 text-base font-bold md:text-lg' style={{ color: TEXT_LIGHT, fontFamily: 'var(--font-bai)' }}>
                {t(`hyperspace.cards.${i}.title`)}
              </h3>
              <p className='text-sm leading-relaxed' style={{ color: STAR_GRAY, fontFamily: 'var(--font-outfit)' }}>
                {t(`hyperspace.cards.${i}.desc`)}
              </p>
            </HoloCard>
          ))}
        </div>
      </Section>

      {/* SECTION 5 - SHIELD GENERATOR */}
      <Section id='shield-generator'>
        <SectionTitle subtitle={t('shield.subtitle')} title={t('shield.section_title')} description={t('shield.section_description')} accentColor={SABER_BLUE} />
        <ShieldGenerator />
      </Section>

      {/* SECTION 6 - ARSENAL BAY */}
      <Section id='arsenal' bg={<HyperspaceBg />}>
        <SectionTitle subtitle={t('arsenal.subtitle')} title={t('arsenal.title')} description={t('arsenal.description')} accentColor={SABER_GREEN} />
        <div className='grid gap-5 sm:grid-cols-2 lg:grid-cols-3'>
          {Array.from({ length: SW_TOOLS_COUNT }, (_, i) => (
            <HoloCard key={i} className='p-5 md:p-6' delay={i * 0.08} glow={SABER_BLUE}>
              <div className='flex items-start gap-4'>
                <div className='flex size-12 shrink-0 items-center justify-center rounded-lg text-2xl' style={{ backgroundColor: `${SABER_GREEN}10`, border: `1px solid ${SABER_GREEN}25` }}>
                  {t(`arsenal.tools.${i}.icon`)}
                </div>
                <div>
                  <h3 className='mb-1 text-base font-bold' style={{ color: TEXT_LIGHT, fontFamily: 'var(--font-bai)' }}>
                    {t(`arsenal.tools.${i}.name`)}
                  </h3>
                  <p className='mb-1 text-xs' style={{ color: SABER_GREEN, fontFamily: 'var(--font-outfit)' }}>
                    {t(`arsenal.tools.${i}.real_name`)}
                  </p>
                  <p className='text-sm' style={{ color: STAR_GRAY, fontFamily: 'var(--font-outfit)' }}>
                    {t(`arsenal.tools.${i}.description`)}
                  </p>
                </div>
              </div>
            </HoloCard>
          ))}
        </div>
      </Section>

      {/* SECTION 6.5 - PRO PLAYERS (JEDI MASTERS) */}
      <Section id='jedi-masters'>
        <SectionTitle subtitle={t('pros.subtitle')} title={t('pros.title')} description={t('pros.description')} accentColor={SABER_GREEN} />
        <div className='grid gap-5 md:grid-cols-2 lg:grid-cols-3'>
          {PRO_ENDORSEMENTS.map((endorsement, i) => (
            <HoloCard key={endorsement.name} className='p-5' glow={SABER_BLUE} delay={i * 0.08}>
              <div className='mb-3 flex items-center gap-3'>
                <div className='size-12 shrink-0 overflow-hidden rounded-full border-2' style={{ borderColor: `${SABER_GREEN}40` }}>
                  <Image src={endorsement.image} alt={endorsement.name} width={48} height={48} className='size-full object-cover' />
                </div>
                <div>
                  <h3 className='text-base font-bold' style={{ color: TEXT_LIGHT, fontFamily: 'var(--font-bai)' }}>
                    {endorsement.name}
                  </h3>
                  <p className='text-xs' style={{ color: SABER_GREEN, fontFamily: 'var(--font-outfit)' }}>
                    {t(`pros.items.${i}.title`)}
                  </p>
                  <p className='text-xs' style={{ color: STAR_GRAY, fontFamily: 'var(--font-outfit)' }}>
                    {t(`pros.items.${i}.game`)}
                  </p>
                </div>
              </div>
              <p className='text-sm italic leading-relaxed' style={{ color: STAR_GRAY, fontFamily: 'var(--font-outfit)' }}>
                &ldquo;{t(`pros.items.${i}.quote`)}&rdquo;
              </p>
            </HoloCard>
          ))}
        </div>
      </Section>

      {/* SECTION 7 - SUPPORTED GALAXIES */}
      <Section id='supported-galaxies'>
        <SectionTitle subtitle={t('galaxies.subtitle')} title={t('galaxies.title')} description={t('galaxies.description')} accentColor={SABER_BLUE} />
        <div className='relative overflow-hidden' style={{ maskImage: 'linear-gradient(to right, transparent, black 8%, black 92%, transparent)', WebkitMaskImage: 'linear-gradient(to right, transparent, black 8%, black 92%, transparent)' }}>
          <motion.div className='flex gap-4' animate={{ x: [0, -2520] }} transition={{ x: { duration: 30, repeat: Infinity, ease: 'linear' as const } }}>
            {[...GAME_IMAGES, ...GAME_IMAGES].map((game, i) => (
              <div key={`${game.name}-${i}`} className='w-[160px] shrink-0 cursor-pointer overflow-hidden rounded-xl border transition-all duration-300 hover:scale-105 md:w-[180px]' style={{ borderColor: `${SABER_BLUE}20`, backgroundColor: SPACE_BLACK }}>
                <div className='relative'>
                  <Image src={`/images/games/${game.file}`} alt={game.name} width={180} height={240} className='h-auto w-full object-cover' />
                  <div className='absolute inset-x-0 bottom-0 p-1.5 text-center' style={{ background: `linear-gradient(transparent, ${SPACE_BLACK}E0)` }}>
                    <span className='text-xs font-semibold' style={{ color: TEXT_LIGHT, fontFamily: 'var(--font-outfit)' }}>
                      {game.name}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </motion.div>
        </div>
        <motion.p className='mt-6 text-center text-sm' style={{ color: STAR_GRAY, fontFamily: 'var(--font-outfit)' }} initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} transition={{ delay: 0.3, duration: 0.5, ease: EASE_OUT }} viewport={{ once: true }}>
          {t('galaxies.more_games')}
        </motion.p>
      </Section>

      {/* SECTION 8 - FAQ */}
      <Section id='faq'>
        <SectionTitle subtitle={t('faq.subtitle')} title={t('faq.title')} description={t('faq.description')} accentColor={SABER_BLUE} />
        <div className='mx-auto max-w-3xl'>
          <FaqAccordion items={faqItems} accentColor={SABER_BLUE} />
        </div>
      </Section>

      {/* SECTION 9 - FINAL CTA */}
      <section className='relative flex min-h-screen items-center overflow-hidden px-4 md:px-8' style={{ zIndex: 1 }}>
        <HyperspaceBg />
        <div className='pointer-events-none absolute inset-0' style={{ background: `radial-gradient(ellipse 50% 40% at 30% 50%, ${SABER_GREEN}02, transparent), radial-gradient(ellipse 50% 50% at 70% 50%, ${SABER_BLUE}02, transparent)` }} />
        <motion.div className='absolute inset-x-0 top-0 h-px' style={{ background: `linear-gradient(90deg, transparent, ${SABER_GREEN}20, ${SABER_BLUE}20, transparent)` }} animate={{ opacity: [0.2, 0.5, 0.2] }} transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' as const }} />

        <div className='relative mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-10 py-16 lg:grid-cols-2 lg:gap-16' style={{ zIndex: 1 }}>
          <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, ease: EASE_OUT }} viewport={{ once: true }}>
            <div className='mb-5 inline-flex items-center gap-2 rounded-full border px-4 py-1.5' style={{ borderColor: `${SABER_GREEN}30`, backgroundColor: `${SABER_GREEN}08` }}>
              <motion.div className='size-2 rounded-full' style={{ backgroundColor: SABER_GREEN }} animate={{ opacity: [1, 0.3, 1] }} transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' as const }} />
              <span className='text-xs font-semibold uppercase tracking-wider' style={{ color: SABER_GREEN, fontFamily: 'var(--font-outfit)' }}>
                {t('final_cta.badge')}
              </span>
            </div>

            <h2 className='mb-4 text-3xl font-bold leading-tight md:text-4xl lg:text-5xl' style={{ fontFamily: 'var(--font-bai)', color: TEXT_LIGHT }}>
              {t('final_cta.title_line1')}
              <br />
              {t('final_cta.title_line2_prefix')} <span style={{ color: SABER_GREEN }}>{t('final_cta.title_line2_accent')}</span>
            </h2>

            <p className='mb-6 max-w-lg text-base leading-relaxed md:text-lg' style={{ color: STAR_GRAY, fontFamily: 'var(--font-outfit)' }}>
              {t('final_cta.description')}
            </p>

            <div className='mb-6 flex items-center gap-8'>
              {['gamers', 'servers', 'countries'].map((key) => (
                <div key={key}>
                  <p className='text-lg font-bold' style={{ color: SABER_GREEN, fontFamily: 'var(--font-bai)' }}>
                    {t(`final_cta.stat_${key}_value`)}
                  </p>
                  <p className='text-[10px] uppercase tracking-wider' style={{ color: STAR_GRAY, fontFamily: 'var(--font-outfit)' }}>
                    {t(`final_cta.stat_${key}`)}
                  </p>
                </div>
              ))}
            </div>

            <p className='text-sm italic' style={{ color: `${STAR_GRAY}A0`, fontFamily: 'var(--font-outfit)' }}>
              {t('final_cta.quote')}
            </p>
          </motion.div>

          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} transition={{ duration: 0.7, delay: 0.15, ease: EASE_OUT }} viewport={{ once: true }}>
            <HoloCard className='p-6 md:p-8' glow={SABER_GREEN} opaque>
              <div className='mb-5 text-center'>
                <h3 className='mb-1 text-xl font-bold md:text-2xl' style={{ fontFamily: 'var(--font-bai)', color: TEXT_LIGHT }}>
                  {t('hero.form_title')}
                </h3>
                <p className='text-sm' style={{ color: STAR_GRAY, fontFamily: 'var(--font-outfit)' }}>
                  {t('hero.form_subtitle')}
                </p>
              </div>
              <CreateAccountV2 daysTrial={daysTrial} customTrialData={{ custom: trialConfigId, trialPageId }} isDisabledButton={isDisabledButton} formId='v4b-final-form' accentColor={SABER_GREEN} />

              <div className='mt-5 grid grid-cols-3 gap-3 pt-5' style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                {[
                  { val: t('final_cta.stat_ping_value'), lbl: t('final_cta.stat_ping_label') },
                  { val: t('final_cta.stat_servers_value'), lbl: t('final_cta.stat_servers') },
                  { val: t('final_cta.stat_gamers_value'), lbl: t('final_cta.stat_gamers') },
                ].map((s) => (
                  <div key={s.lbl} className='text-center'>
                    <p className='text-sm font-bold' style={{ color: SABER_GREEN, fontFamily: 'var(--font-bai)' }}>
                      {s.val}
                    </p>
                    <p className='mt-0.5 text-[10px]' style={{ color: STAR_GRAY, fontFamily: 'var(--font-outfit)' }}>
                      {s.lbl}
                    </p>
                  </div>
                ))}
              </div>
            </HoloCard>
          </motion.div>
        </div>
      </section>

      <Footer text={t('footer', { year: new Date().getFullYear() })} />
    </div>
  );
}
